import { useState } from "react";
import {
  Alert,
  Card,
  DataTable,
  PageHeader,
  Spinner,
} from "@empite/ui-components";
import type {
  Reason,
  TenantPlanChanged,
  TenantSubscriptionState,
  TenantTier,
  TenantTierUsage,
  TenantUpgradeCheckout,
} from "../admin-client";
import {
  useCancelTenantPlan,
  useChangeTenantPlan,
  useStartTenantUpgrade,
  useTenantBilling,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { BillingNoticeList } from "./billing-notices";
import { Money, StatusBadge, when } from "./common";
import { intervalLabel, tokenCount } from "./tiers";

/**
 * Billing — what THIS company pays the platform, how much of the allowance is
 * gone, and the checkout that buys a bigger one.
 *
 * THE MIRROR OF Companies AND Tiers, and deliberately not a filter on either.
 * The tenant is never named in the request: it comes off the session's scope,
 * so there is no shape of this screen that reads another company's consumption
 * or puts another company's card on a tier. That is why a tenant operator has
 * this page and not those two, and why platform staff — who belong to no
 * company — have those two and not this one.
 *
 * THE NUMBER ON THIS PAGE IS THE NUMBER THE GATEWAY BLOCKS ON. It is committed
 * tokens — settled usage plus every hold still in the air — read through the
 * same meter internal/billing's reservation guard compares against the
 * allowance. Drawing settled usage alone would show room at the exact moment
 * traffic starts being refused.
 *
 * BUYING A TIER HERE MOVES NOTHING. The 201 is a hosted page to pay at, the
 * same as a customer top-up's; the tier changes when the provider's signed
 * webhook arrives, and then it changes immediately — mid-period, without
 * waiting for a renewal.
 */
export function Billing() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, "tenant_billing:read") ? (
          <TenantBill mayUpgrade={can(session.operator, "tenant_billing:manage")} />
        ) : (
          <>
            <PageHeader title="Billing" />
            <Card>
              <Alert tone="info" title="This is a company's own bill">
                <p>
                  It is the tier <em>your company</em> is on and what it has spent
                  against it, so it belongs to a session that belongs to a
                  company. A platform session belongs to none — every tenant&rsquo;s
                  tier and consumption is on <strong>Companies</strong>, and the
                  price list they are all sold from is on <strong>Tiers</strong>.
                </p>
              </Alert>
            </Card>
          </>
        )
      }
    </RequireOperator>
  );
}

/**
 * How much of the allowance is gone, as a percentage of the COMMITTED figure.
 *
 * An allowance of zero reads as 100% rather than as a division by zero: a tier
 * that includes no tokens is exhausted from the moment it opens, which is what
 * the gateway already believes about it.
 */
function percentUsed(usage: TenantTierUsage): number {
  if (usage.tokens_allowance === 0) {
    return 100;
  }
  return Math.min(
    100,
    Math.round((usage.committed_tokens / usage.tokens_allowance) * 100),
  );
}

/** A one-time grant is stored as ending at the year-9999 horizon rather than
 *  as a NULL — see tenancy.PerpetualPeriodEnd — so it is said in words instead
 *  of printed as a date nobody would believe. */
function periodEnd(value: string): string {
  return value.startsWith("9999") ? "never expires" : when(value);
}

function AllowanceMeter({ usage }: { usage: TenantTierUsage }) {
  const percent = percentUsed(usage);
  return (
    <div className="esm-allowance">
      <div
        className={
          usage.exhausted ? "esm-allowance__track esm-allowance__track--spent" : "esm-allowance__track"
        }
      >
        <div className="esm-allowance__fill" style={{ width: `${percent}%` }} />
      </div>
      <dl className="esm-detail-list">
        <dt>Committed</dt>
        <dd>
          {tokenCount(usage.committed_tokens)} of {tokenCount(usage.tokens_allowance)}{" "}
          <span className="esm-muted-text">({percent}%)</span>
        </dd>
        {/* Settled and in-flight are shown APART rather than folded together,
            because they are different kinds of claim: one is what a provider
            billed and reconciled, the other is what has been promised to a
            request that has not come back yet. Their sum is the row above, and
            that sum is what is compared against the allowance. */}
        <dt>Settled usage</dt>
        <dd>{tokenCount(usage.tokens_used)}</dd>
        <dt>In flight</dt>
        <dd>{tokenCount(usage.in_flight_tokens)}</dd>
        <dt>Remaining</dt>
        <dd>{tokenCount(usage.remaining_tokens)}</dd>
        <dt>Period</dt>
        <dd>
          {when(usage.period_start)} &rarr; {periodEnd(usage.period_end)}
        </dd>
      </dl>
    </div>
  );
}

function TenantBill({ mayUpgrade }: { mayUpgrade: boolean }) {
  const billing = useTenantBilling();
  const upgrade = useStartTenantUpgrade();
  const cancel = useCancelTenantPlan();
  const [checkout, setCheckout] = useState<TenantUpgradeCheckout | null>(null);

  if (billing.isPending) {
    return <Spinner label="Loading your bill" />;
  }
  if (billing.error) {
    return <ErrorAlert error={billing.error} title="Could not load your billing" />;
  }

  const view = billing.data;
  if (!view) {
    return null;
  }
  const { plan, subscription, usage, offers, lapsed } = view;
  // A once tier (the free grant) never renews, so there is nothing to cancel;
  // and a tier already set to end has been cancelled — the action goes, and
  // the end date takes its place. past_due stays cancellable: a company whose
  // renewal failed is exactly the one that may want the retries to stop, and
  // TenantBilling.Cancel refuses no status.
  const recurring = plan ? plan.billing_interval !== "once" : false;
  const cancellable =
    mayUpgrade &&
    recurring &&
    subscription !== undefined &&
    !subscription.cancel_at_period_end &&
    (subscription.status === "active" ||
      subscription.status === "trialing" ||
      subscription.status === "past_due");

  async function buy(tier: TenantTier, reason: Reason) {
    const result = await upgrade.mutateAsync({ planSlug: tier.slug, reason });
    setCheckout(result);
  }

  return (
    <>
      <PageHeader
        title="Billing"
        description={`What ${view.tenant.name} pays the platform, and how much of this period's token allowance is committed — settled usage plus the reservations still in the air, which is the sum the gateway refuses on.`}
      />

      {usage?.exhausted ? (
        <Alert tone="danger" title="Your allowance is spent">
          <p>
            New requests are being refused at the <strong>reservation</strong>{" "}
            step — before a prompt leaves the building, not at settlement — so
            nothing is being charged for work that will not run. Your customers&rsquo;
            wallets are untouched and still funded; this is your company&rsquo;s
            operator plan, not their balance. Moving to a paid tier below lifts
            the block on the next request, mid-period.
          </p>
        </Alert>
      ) : null}

      {subscription?.status === "past_due" ? (
        <Alert tone="danger" title="Your tier payment failed">
          <p>
            The renewal charge did not go through. Your tier stays in force
            until <strong>{when(subscription.grace_until)}</strong>; if the
            payment has not succeeded by then, the tier expires and new
            requests are refused at reservation. Update the card with the
            payment provider and the next retry settles it.
          </p>
        </Alert>
      ) : null}

      {subscription?.cancel_at_period_end ? (
        <Alert tone="warning" title="Your tier is cancelled">
          <p>
            It will not renew. Everything you paid for stays in force until{" "}
            <strong>{when(subscription.current_period_end)}</strong>, and then
            the tier ends.
          </p>
        </Alert>
      ) : null}

      {checkout ? (
        <Alert tone="info" title={`Pay for ${checkout.plan.name} to activate it`}>
          <p>
            Nothing has moved yet. This is a hosted page to pay on, and your tier
            changes when the payment provider confirms the charge — at which
            point the new allowance applies immediately, without waiting for a
            renewal.
          </p>
          <p>
            <a href={checkout.checkout.url} target="_blank" rel="noreferrer">
              Open the checkout for {checkout.checkout.price.currency}{" "}
              {checkout.checkout.price.amount}
            </a>{" "}
            <span className="esm-muted-text">
              (expires {when(checkout.checkout.expires_at)})
            </span>
          </p>
        </Alert>
      ) : null}

      <Card title="Your tier">
        {plan ? (
          <dl className="esm-detail-list">
            <dt>Tier</dt>
            <dd>
              {plan.name} <StatusBadge status={plan.status} />
              {plan.description ? (
                <div className="esm-muted-text">{plan.description}</div>
              ) : null}
            </dd>
            <dt>Price</dt>
            <dd>
              <Money value={plan.price} />{" "}
              <span className="esm-muted-text">{intervalLabel(plan)}</span>
            </dd>
            {/* A COUNT, never money. There is no exchange rate between this and
                the price above and none should be inferred from them sitting on
                the same tier. */}
            <dt>Included tokens</dt>
            <dd>
              {tokenCount(plan.included_tokens)}{" "}
              <span className="esm-muted-text">{intervalLabel(plan)}</span>
            </dd>
            {subscription ? (
              <>
                <dt>Subscription</dt>
                <dd>
                  <StatusBadge status={subscription.status} />
                  {subscription.cancel_at_period_end ? (
                    <>
                      {" "}
                      <StatusBadge status="cancelling" />
                    </>
                  ) : null}
                </dd>
                <dt>Current period</dt>
                <dd>
                  {when(subscription.current_period_start)} &rarr;{" "}
                  {periodEnd(subscription.current_period_end)}
                </dd>
                {subscription.status === "past_due" ? (
                  <>
                    <dt>Grace ends</dt>
                    <dd>
                      <strong>{when(subscription.grace_until)}</strong>
                    </dd>
                  </>
                ) : null}
                {/* next_renewal is NULL when nothing will renew, and that is
                    said in words: a cancelled tier ends, a once tier never
                    bills again. */}
                <dt>{subscription.cancel_at_period_end ? "Ends" : "Next renewal"}</dt>
                <dd>
                  {subscription.next_renewal ? (
                    <>
                      <Money value={subscription.next_renewal} />{" "}
                      <span className="esm-muted-text">
                        on {when(subscription.current_period_end)}
                      </span>
                    </>
                  ) : subscription.cancel_at_period_end ? (
                    when(subscription.current_period_end)
                  ) : (
                    <span className="esm-muted-text">does not renew</span>
                  )}
                </dd>
              </>
            ) : null}
          </dl>
        ) : lapsed ? (
          // NOT the unmetered state below, though both have no current row:
          // this company's paid tier ran out and the gateway refuses it.
          <Alert tone="danger" title="Your tier has expired">
            <p>
              The paid tier this company was on has ended, so new requests are
              refused at reservation. Buy a tier below to start again — it
              applies as soon as the payment provider confirms the charge.
            </p>
          </Alert>
        ) : (
          // NOT an allowance of zero. An unmetered tenant is not a blocked one,
          // and inventing a limit here would make this page disagree with the
          // gateway about who is refused.
          <Alert tone="info" title="This company is not metered">
            <p>
              It is on no operator tier, so nothing is counting its tokens and
              nothing is refusing them. The platform&rsquo;s own tenant is in
              exactly this state.
            </p>
          </Alert>
        )}
      </Card>

      {cancellable && subscription ? (
        <Card title="Cancel your tier">
          <ErrorAlert error={cancel.error} title="The tier was not cancelled" />
          <p>
            Cancelling stops the renewal. Nothing is refunded and nothing ends
            now: the tier stays in force until{" "}
            {when(subscription.current_period_end)}.
          </p>
          <div className="esm-actions">
            <ReasonButton
              variant="danger"
              busy={cancel.isPending}
              mutation={{
                title: `Cancel ${plan?.name ?? "your tier"}`,
                tone: "danger",
                description: (
                  <p>
                    Your tier will not renew. It stays in force until{" "}
                    {when(subscription.current_period_end)}, then ends and new
                    requests are refused.
                  </p>
                ),
                confirmLabel: "Cancel tier",
                run: async (reason) => {
                  await cancel.mutateAsync(reason);
                },
              }}
            >
              Cancel tier
            </ReasonButton>
          </div>
        </Card>
      ) : null}

      <BillingNoticeList />

      {usage ? (
        <Card title="This period">
          <AllowanceMeter usage={usage} />
        </Card>
      ) : null}

      {/* A provider-backed tier moves by re-pricing its subscription; a second
          checkout would bill twice (StartUpgrade answers 409). So a company on
          a recurring tier is offered change tier, and only one without is
          offered a checkout. */}
      {recurring && subscription && plan ? (
        <ChangeTier
          current={plan}
          subscription={subscription}
          offers={offers}
          mayChange={mayUpgrade}
        />
      ) : (
            <Card title={lapsed ? "Buy a tier again" : "Move to a bigger tier"}>
        <ErrorAlert error={upgrade.error} title="The checkout was not opened" />
        {!mayUpgrade ? (
          <Alert tone="info" title="Buying a tier is the owner's call">
            <p>
              You can see what the company is spending, which is what makes it
              possible to ask for an upgrade — but putting the company&rsquo;s card
              on a tier needs <code>tenant_billing:manage</code>, which only a
              company owner holds.
            </p>
          </Alert>
        ) : null}
        <DataTable<TenantTier>
          rows={offers}
          rowKey={(row) => row.id}
          empty="There is nothing on sale above your current tier."
          columns={[
            {
              key: "name",
              header: "Tier",
              render: (row) => (
                <>
                  <div>{row.name}</div>
                  {row.description ? (
                    <span className="esm-muted-text">{row.description}</span>
                  ) : (
                    <code className="esm-muted-text">{row.slug}</code>
                  )}
                </>
              ),
            },
            {
              key: "price",
              header: "Price",
              render: (row) => (
                <>
                  <Money value={row.price} />{" "}
                  <span className="esm-muted-text">{intervalLabel(row)}</span>
                </>
              ),
            },
            {
              key: "tokens",
              header: "Included tokens",
              align: "end",
              render: (row) => <span>{tokenCount(row.included_tokens)}</span>,
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) =>
                mayUpgrade ? (
                  <div className="esm-actions esm-actions--table">
                    <ReasonButton
                      variant="primary"
                      busy={upgrade.isPending}
                      mutation={{
                        title: `Buy ${row.name}`,
                        // "primary" and not "danger": this ends nothing and
                        // charges nothing by itself. It opens a page the
                        // operator still has to pay on.
                        tone: "primary",
                        description: (
                          <p>
                            This opens a hosted checkout for{" "}
                            {row.price.currency} {row.price.amount}{" "}
                            {intervalLabel(row)}. Nothing is charged and your
                            tier does not move until the payment provider
                            confirms it — and then{" "}
                            {tokenCount(row.included_tokens)} tokens apply
                            immediately, mid-period.
                          </p>
                        ),
                        confirmLabel: "Open checkout",
                        run: (reason) => buy(row, reason),
                      }}
                    >
                      Upgrade
                    </ReasonButton>
                  </div>
                ) : null,
            },
          ]}
        />
      </Card>
      )}
    </>
  );
}

/**
 * Change tier — move a live recurring tier onto another one NOW, up or down,
 * with the provider invoicing the proration. The tiers listed are the ones
 * TenantBilling.ChangeTier would accept: same renewal schedule, same currency.
 * The buttons are disabled rather than hidden while the subscription is not
 * active or is cancelling, so the operator reads WHY they cannot move.
 */
function ChangeTier({
  current,
  subscription,
  offers,
  mayChange,
}: {
  current: TenantTier;
  subscription: TenantSubscriptionState;
  offers: TenantTier[];
  mayChange: boolean;
}) {
  const change = useChangeTenantPlan();
  const [moved, setMoved] = useState<TenantPlanChanged | null>(null);
  const candidates = offers.filter(
    (tier) =>
      tier.id !== current.id &&
      tier.billing_interval === current.billing_interval &&
      tier.interval_count === 1 &&
      tier.price.currency === current.price.currency,
  );
  const blocked = subscription.cancel_at_period_end
    ? "This tier is cancelled and ends at the period end, so it cannot be changed. Buy a tier again once it has ended."
    : subscription.status !== "active"
      ? "The tier can only be changed while its subscription is active. Settle the failed payment first."
      : null;

  return (
    <Card title="Change tier">
      <ErrorAlert error={change.error} title="The tier was not changed" />
      {moved ? (
        <Alert
          tone="success"
          title={
            moved.applied
              ? `You are now on ${moved.plan.name}`
              : `Moving to ${moved.plan.name}`
          }
        >
          <p>
            {moved.applied
              ? "The payment provider has invoiced the difference and the new allowance applies now."
              : "The payment provider is invoicing the difference. The tier above changes when it confirms the charge."}
          </p>
        </Alert>
      ) : null}
      {!mayChange ? (
        <Alert tone="info" title="Changing tier is the owner's call">
          <p>
            Moving the company&rsquo;s tier needs <code>tenant_billing:manage</code>,
            which only a company owner holds.
          </p>
        </Alert>
      ) : blocked ? (
        <Alert tone="warning" title="The tier cannot be changed right now">
          <p>{blocked}</p>
        </Alert>
      ) : null}
      <DataTable<TenantTier>
        rows={candidates}
        rowKey={(row) => row.id}
        empty="There is no other tier on the same schedule to move to."
        columns={[
          {
            key: "name",
            header: "Tier",
            render: (row) => (
              <>
                <div>{row.name}</div>
                {row.description ? (
                  <span className="esm-muted-text">{row.description}</span>
                ) : (
                  <code className="esm-muted-text">{row.slug}</code>
                )}
              </>
            ),
          },
          {
            key: "price",
            header: "Price",
            render: (row) => (
              <>
                <Money value={row.price} />{" "}
                <span className="esm-muted-text">{intervalLabel(row)}</span>
              </>
            ),
          },
          {
            key: "tokens",
            header: "Included tokens",
            align: "end",
            render: (row) => <span>{tokenCount(row.included_tokens)}</span>,
          },
          {
            key: "actions",
            header: "",
            align: "end",
            render: (row) => {
              if (!mayChange) {
                return null;
              }
              // Direction by allowance, a count — never by comparing prices
              // as numbers.
              const upgrade = row.included_tokens > current.included_tokens;
              const verb = upgrade ? "Upgrade" : "Downgrade";
              return (
                <div className="esm-actions esm-actions--table">
                  <ReasonButton
                    variant={upgrade ? "primary" : "secondary"}
                    busy={change.isPending}
                    disabled={blocked !== null}
                    mutation={{
                      title: `${verb} to ${row.name}`,
                      tone: "primary",
                      description: (
                        <p>
                          Your tier moves from {current.name} to {row.name} now,
                          at {row.price.currency} {row.price.amount}{" "}
                          {intervalLabel(row)}. The payment provider invoices
                          the prorated difference immediately and{" "}
                          {tokenCount(row.included_tokens)} tokens apply as soon
                          as it confirms.
                        </p>
                      ),
                      confirmLabel: verb,
                      run: async (reason) => {
                        setMoved(null);
                        setMoved(await change.mutateAsync({ planSlug: row.slug, reason }));
                      },
                    }}
                  >
                    {verb}
                  </ReasonButton>
                </div>
              );
            },
          },
        ]}
      />
    </Card>
  );
}
